import { useState } from 'react';
import { KeyRound, ShieldCheck, Eye, EyeOff, Mail } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function Account() {
  const { admin } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  useEffect(() => { api.get('/admin/me').then(r => setRecovery(r.data.email || '')).catch(() => {}); }, []);

  const saveEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try { const r = await api.put('/admin/email', { email: recovery }); setRecovery(r.data.email); toast.success(r.data.email ? "Adresse de récupération enregistrée" : "Adresse de récupération retirée"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingEmail(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (form.new_password !== form.confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setSaving(true);
    try {
      await api.put('/admin/password', { current_password: form.current_password, new_password: form.new_password });
      toast.success("Mot de passe modifié. Utilisez-le lors de votre prochaine connexion.");
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, testId) => (
    <div>
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <Input type={show ? 'text' : 'password'} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="mt-1 rounded-lg" autoComplete={key === 'current_password' ? 'current-password' : 'new-password'} data-testid={testId} />
    </div>
  );

  return (
    <div data-testid="admin-account" className="max-w-xl">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">Mon compte</h1>
      <p className="text-slate-500 mt-1">Connecté en tant que <span className="font-semibold text-slate-900" data-testid="account-username">{admin?.username}</span></p>

      <form onSubmit={saveEmail} className="bg-white border border-slate-200 rounded-2xl p-6 mt-8" data-testid="recovery-email-form">
        <div className="flex items-center gap-2 mb-1"><Mail className="h-5 w-5 text-violet-600" /><h2 className="text-base md:text-lg font-bold text-slate-900">Email de récupération</h2></div>
        <p className="text-sm text-slate-500 mb-4">Indispensable pour utiliser « Mot de passe oublié » : le lien de réinitialisation sera envoyé à cette adresse.</p>
        <div className="flex gap-3">
          <Input type="email" value={recovery} onChange={e => setRecovery(e.target.value)} placeholder="vous@plumeetpion.fr" className="rounded-lg flex-1" data-testid="recovery-email-input" />
          <Button type="submit" disabled={savingEmail} className="rounded-full" data-testid="recovery-email-save-btn">{savingEmail ? '...' : 'Enregistrer'}</Button>
        </div>
        {!recovery && <p className="text-xs text-amber-600 mt-2" data-testid="recovery-email-warning">Aucune adresse enregistrée : la réinitialisation par email est impossible pour l'instant.</p>}
      </form>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="password-form">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-violet-600" /><h2 className="text-base md:text-lg font-bold text-slate-900">Changer mon mot de passe</h2></div>
        <p className="text-sm text-slate-500 mb-5 flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" /> Votre mot de passe est stocké chiffré : personne ne peut le lire, pas même l'équipe technique.</p>
        <div className="space-y-4">
          {field('current_password', 'Mot de passe actuel', 'current-password-input')}
          {field('new_password', 'Nouveau mot de passe (8 caractères minimum)', 'new-password-input')}
          {field('confirm', 'Confirmer le nouveau mot de passe', 'confirm-password-input')}
        </div>
        <div className="flex items-center justify-between mt-6">
          <button type="button" onClick={() => setShow(s => !s)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5" data-testid="toggle-password-visibility">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {show ? 'Masquer' : 'Afficher'}
          </button>
          <Button type="submit" disabled={saving} className="rounded-full" data-testid="change-password-btn">{saving ? 'Enregistrement...' : 'Modifier le mot de passe'}</Button>
        </div>
      </form>
    </div>
  );
}
